import React, { useState } from 'react';
import { useT } from '../i18n/LocaleContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Avatar from '../components/ui/Avatar';
import FormField from '../components/ui/FormField';
import Input from '../components/ui/Input';
import { changePasswordRequest } from '../api/auth';
import { useApi } from '../api/client';
import { formatRelativeTime } from '../utils/format';

const ROLE_TONE = { admin: 'admin', tresorier: 'tresorier', coordinateur: 'coordinateur', secretaire: 'pelerinage' };

export default function ProfilePage() {
  const t = useT();
  const toast = useToast();
  const { user } = useAuth();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  if (!user) return null;

  const validate = () => {
    const e = {};
    if (!current.trim()) e.current = t('validation.required');
    if (!next || next.length < 8) e.next = t('profile.password_min');
    if (next !== confirm) e.confirm = t('profile.password_mismatch');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    if (!useApi) {
      toast.warning(t('profile.api_mode_off'));
      return;
    }
    setSaving(true);
    try {
      await changePasswordRequest({ currentPassword: current, newPassword: next });
      toast.success(t('profile.toast_password_changed'));
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      toast.error(err?.message || t('toast.generic_error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
      <Card title={t('profile.section_account')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '18px' }}>
          <Avatar firstName={user.firstName} lastName={user.lastName} size="lg" tone="gold" />
          <div>
            <div style={{ fontFamily: "'Cinzel', serif", fontSize: '16px', color: 'var(--blue-deep)' }}>
              {user.firstName} {user.lastName}
            </div>
            <div className="mono" style={{ marginTop: '4px' }}>{user.username}</div>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <Badge tone={ROLE_TONE[user.role] || 'admin'}>
              {t(`users.roles.${user.role}`, {})}
            </Badge>
          </div>
        </div>

        <div className="detail-grid">
          <div>
            <div className="detail-item-label">{t('common.email')}</div>
            <div className="detail-item-value">{user.email || '—'}</div>
          </div>
          <div>
            <div className="detail-item-label">{t('common.phone')}</div>
            <div className="detail-item-value">{user.phone || '—'}</div>
          </div>
          <div>
            <div className="detail-item-label">{t('profile.last_login')}</div>
            <div className="detail-item-value">{user.lastLoginAt ? formatRelativeTime(user.lastLoginAt, t) : '—'}</div>
          </div>
          <div>
            <div className="detail-item-label">{t('profile.member_since')}</div>
            <div className="detail-item-value">{user.createdAt ? formatRelativeTime(user.createdAt, t) : '—'}</div>
          </div>
        </div>
      </Card>

      <Card title={t('profile.section_security')}>
        {!useApi && (
          <div className="form-hint" style={{ marginBottom: '14px', fontStyle: 'normal' }}>
            ⚠️ {t('profile.api_mode_off')}
          </div>
        )}
        <form onSubmit={handleSubmit} noValidate>
          <FormField label={t('profile.current_password')} htmlFor="cur" required error={errors.current}>
            <Input id="cur" type="password" autoComplete="current-password" value={current}
                   onChange={(e) => setCurrent(e.target.value)} invalid={Boolean(errors.current)} />
          </FormField>
          <FormField label={t('profile.new_password')} htmlFor="new" required error={errors.next} hint={t('profile.password_min')}>
            <Input id="new" type="password" autoComplete="new-password" value={next}
                   onChange={(e) => setNext(e.target.value)} invalid={Boolean(errors.next)} />
          </FormField>
          <FormField label={t('profile.confirm_new_password')} htmlFor="conf" required error={errors.confirm}>
            <Input id="conf" type="password" autoComplete="new-password" value={confirm}
                   onChange={(e) => setConfirm(e.target.value)} invalid={Boolean(errors.confirm)} />
          </FormField>
          <Button variant="gold" onClick={handleSubmit} loading={saving} disabled={!useApi}>
            {t('profile.change_password')}
          </Button>
        </form>
      </Card>
    </div>
  );
}
